// The model registers quotes with validate_citation and refers to them as [label] in its reply.
// The backend bakes the labels into ${A|E|D:...} citations only when it saves the message
// (bake_citations in backend/src/utils/agent_loop.py), so the stream carries raw labels.
// Resolving them here with byte-for-byte the same output lets the committed message match
// the streamed one.

import type { ChatMessage } from "@/app/typing/chat";

type CitationPrefix = "A" | "E" | "D";

type RegisteredCitation = { id: number; start_line: number; end_line: number; content: string };

/** Registered citations by label, per source type, in registration order. */
export type CitationRegistry = Record<CitationPrefix, Map<string, RegisteredCitation>>;

type BatchEntry = {
    label?: string;
    artifact_id?: number;
    event_id?: number;
    document_id?: number;
    start_line: number;
    end_line: number;
    content?: string;
};

export function emptyCitationRegistry(): CitationRegistry {
    return { A: new Map(), E: new Map(), D: new Map() };
}

/** Collect the citations registered by validate_citation results in these messages. */
export function buildCitationRegistry(messages: ChatMessage[]): CitationRegistry {
    const registry = emptyCitationRegistry();
    for (const msg of messages) {
        if (!Array.isArray(msg.content)) continue;
        for (const item of msg.content) {
            if (item.type !== "tool_result" || item.name !== "validate_citation") continue;
            const batch = (item as { citations_batch?: BatchEntry[] }).citations_batch;
            if (!Array.isArray(batch)) continue;
            for (const cite of batch) {
                if (!cite.label) continue;
                const entry = (id: number) => ({ id, start_line: cite.start_line, end_line: cite.end_line, content: cite.content ?? "" });
                if (cite.artifact_id !== undefined) registry.A.set(cite.label, entry(cite.artifact_id));
                else if (cite.event_id !== undefined) registry.E.set(cite.label, entry(cite.event_id));
                else if (cite.document_id !== undefined) registry.D.set(cite.label, entry(cite.document_id));
            }
        }
    }
    return registry;
}

function hasLabels(registry: CitationRegistry): boolean {
    return registry.A.size + registry.E.size + registry.D.size > 0;
}

/** Replace `[label]` (and the mistaken `${label}`) with its citation, exactly as bake_citations does. */
export function resolveCitationLabels(text: string, registry: CitationRegistry): string {
    if (!hasLabels(registry)) return text;
    for (const prefix of ["A", "E", "D"] as const) {
        for (const [label, cite] of registry[prefix]) {
            const citation = `\${${prefix}:${cite.id}, ${cite.start_line}, ${cite.end_line}, "${cite.content.replaceAll('"', '\\"')}"}`;
            // `${label}` is not a valid reference; it handles bad agent output that mixes the two citation forms.
            text = text.replaceAll(`\${${label}}`, () => citation).replaceAll(`[${label}]`, () => citation);
        }
    }
    return text;
}

const PARTIAL_LABEL = /(?:\[|\$\{)([^\s[\]{}$]*)$/;

/** Drop an unfinished `[label` or `${label` from the end of streamed text, so it never flashes. */
export function trimPartialLabelTail(text: string, registry: CitationRegistry): string {
    const match = PARTIAL_LABEL.exec(text);
    if (!match || !hasLabels(registry)) return text;
    const partial = match[1];
    const known = (["A", "E", "D"] as const).some((prefix) => [...registry[prefix].keys()].some((label) => label.startsWith(partial)));
    return known ? text.slice(0, match.index).trimEnd() : text;
}
